import { Box, SimpleGrid } from "@chakra-ui/react";
import { MenuItem, Pagination, scrollToTop } from "@verivest/shared-components";
import { isClient } from "@verivest/shared-components/";
import { OpportunityStatusEnumType } from "@verivest/shared-components/src/api/enums";
import { apiManagerModel } from "@verivest/shared-components/src/api/hooks/models/useManagerModel";
import useOpportunityModel, {
  apiOpportunityModel,
} from "@verivest/shared-components/src/api/hooks/models/useOpportunityModel";
import { OpportunityCard } from "@verivest/shared-components/src/components/common/opportunities/OpportunityCard";
import { VeriAsyncRouteComponentType } from "@verivest/shared-components/src/defines/after-js-types";
import DynamicConfigFlags from "@verivest/shared-components/src/dynamic-config/configs";
import DynamicConfig from "@verivest/shared-components/src/dynamic-config/DynamicConfig";
import useGetDataOrDefaults from "@verivest/shared-components/src/hooks/data/useGetDataOrDefaults";
import React from "react";
import { InvestorPortalEmptyStatus } from "../../../components/investor-portal/InvestorPortalEmptyStatus";
import { InvestorPortalWhiteLabelPageContainer } from "../../../components/investor-portal/layouts/InvestorPortalWhiteLabelPageContainer";
import { WhiteLabelSortBy } from "../../../components/investor-portal/white-label/WhiteLabelSortBy";
import { ManagerWhiteLabelRouteParams } from "../../../routes/investorPortalPages.types";
import {
  ManagerWhiteLabelRoutesServerInitialProps,
  WhiteLabelPageExtraProps,
} from "./types";

const itemsInARow = 4;
const GET_OPPORTUNITIES_INCLUDE =
  "Manager,Images,BannerImages,BannerImages.CroppedImage,BannerImages.FullSizeImage";
const DEFAULT_SORT = "manager_name";

const availableSortOptions = [
  OpportunityStatusEnumType.Open,
  OpportunityStatusEnumType.FullCycle,
  OpportunityStatusEnumType.Funded,
];

export const OpportunitiesPage: VeriAsyncRouteComponentType<
  ManagerWhiteLabelRoutesServerInitialProps & WhiteLabelPageExtraProps,
  ManagerWhiteLabelRouteParams
> = (props) => {
  const opportunityModel = useOpportunityModel({
    usePlacement: false,
    queryParams: {
      is_investor: "true",
    },
  });
  const [page, setPage] = React.useState(0);

  const [sortBy, setSortBy] = React.useState("Open");

  const { data: opportunities } = useGetDataOrDefaults(
    props.serverOpportunities,
    async () =>
      await opportunityModel.getPagedMany(
        {
          page,
          sort: sortBy.toLowerCase().replace(/ /g, "_"),
          pageSize: 96,
        },
        undefined,
        {
          "filter[manager_id]": String(props.manager?.id),
          include: GET_OPPORTUNITIES_INCLUDE,
        },
      ),
    [page, sortBy],
  );

  const changePage = (page: number) => {
    setPage(page);
    scrollToTop();
    //refreshOpportunities();
  };

  const setFilterOption = (filterOption: string) => {
    setSortBy(filterOption);
  };

  if (!opportunities) {
    return <></>;
  }

  if (opportunities.data.length === 0) {
    return (
      <InvestorPortalEmptyStatus
        title="You have no opportunities"
        subTitle="Once a manager shares an opportunity, your opportunities will appear here."
        imgSrc="/images/investor-portal/empty-status-images/white-label/opportunities.svg"
      />
    );
  }

  return (
    <InvestorPortalWhiteLabelPageContainer
      className={"px-0 sm:py-10"}
      title={"Opportunities"}
      titleRight={
        <WhiteLabelSortBy activeSort={sortBy}>
          {availableSortOptions.map((status) => (
            <MenuItem
              key={status}
              onClick={() => setFilterOption(status)}
              px={8}
            >
              {status}
            </MenuItem>
          ))}
        </WhiteLabelSortBy>
      }
    >
      <SimpleGrid columns={{ base: 1, md: itemsInARow }} spacing={6}>
        {opportunities.data.map((opportunity) => (
          <OpportunityCard
            key={opportunity.id}
            opportunity={opportunity}
            managerIdOrSlug={
              props.manager?.slug || String(props.manager?.id) || ""
            }
            showViewDetailsButton
          />
        ))}
      </SimpleGrid>
      {opportunities.meta.pageTotal > 1 && (
        <Box mt={8}>
          <Pagination
            selected={opportunities.meta.pageNumber - 1}
            pageCount={opportunities.meta.pageTotal}
            onClickBuilder={changePage}
          />
        </Box>
      )}
    </InvestorPortalWhiteLabelPageContainer>
  );
};

OpportunitiesPage.getInitialProps = async (props) => {
  const result: ManagerWhiteLabelRoutesServerInitialProps = {};

  if (isClient()) {
    const opportunityModel = apiOpportunityModel(props);
    const managerModel = apiManagerModel(props);

    const dynamicConfig = new DynamicConfig({});

    const managerId = dynamicConfig.get(DynamicConfigFlags.MANAGER)?.id;

    const manager = (await managerModel.getOne(managerId))?.data;

    result.serverOpportunities = await opportunityModel.getMany(null, {
      "filter[manager_id]": String(manager.id),
      include: GET_OPPORTUNITIES_INCLUDE,
      sort: DEFAULT_SORT,
    });
  }
  return result;
};

OpportunitiesPage.getChunkName = () =>
  "investor-portal-3-manager-white-label-opportunities-page";
